import React from 'react';
import { createPortal } from 'react-dom';
import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { colorClasses } from './utils/colors.js';
import { fetchRooms, fetchDoctors, fetchSchedule, saveAssignment} from './utils/apiCalls.js';
import { useAuth } from './utils/AuthContext.jsx';
import { handleDelete, handleRightClick, handleTouchStart, handleTouchEnd } from './utils/deleteAssignments.js';

export default function CalendarView({ clinicId, clinicName, location }) {
  //Get token for fetches
  const { token } = useAuth();
  //States to manage hovering/color preview
  const [weekOffset, setWeekOffset] = useState(0);
  const [hoveredCell, setHoveredCell] = useState(null);

  // States to manage rooms and doctors
  const [rooms, setRooms] = useState([]);
  const [doctors, setDoctors] = useState([]);
  // State to manage assignments
  const [assignments, setAssignments] = useState({});
  //State to manage context menu to right click -> delete assignments
  const [contextMenu, setContextMenu] = useState(null);


  // reference to the context menu for deleting assignment
  const menuRef = useRef(null);
  // Timer to keep track of how long holding down on a cell - for mobile assignment deletion
  const touchTimer = useRef(null);

  //Find current week
  const getCurrentWeek = (offset = 0) => {
    const startOfWeek = dayjs().startOf('week').add(offset, 'week');
    return Array.from({ length: 7 }, (_, i) => {
      const date = startOfWeek.add(i, 'day');
      return {
        label: date.format('dddd'),
        date: date.format('MMM D'),
        iso: date.format('YYYY-MM-DD'),
      };
    });
  };

  // Toggle week
  const daysOfWeek = getCurrentWeek(weekOffset);

  const prevWeek = () => setWeekOffset((o) => o - 1);
  const nextWeek = () => setWeekOffset((o) => o + 1);

  //MOBILE VIEW
  // Check screen size
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Update check for mobile on screen resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch rooms and doctors fresh when location_id updates
  useEffect(() => {
    if (location?.location_id) {
    fetchRooms(location.location_id, token, setRooms);
    fetchDoctors(location.location_id, token, colorClasses, setDoctors);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Load any assignments already saved
  useEffect(() => {
    if (!location?.location_id) return;

    const start = daysOfWeek[0].iso;
    const end = daysOfWeek[6].iso;

    const mapAssignments = (async () => {
      const data = await fetchSchedule(start, end, location.location_id, token);
      if (!data) return;

      const mapped = {};
      data.assignments.forEach(assignment => {
        if (!mapped[assignment.room_id]) mapped[assignment.room_id] = {};
        mapped[assignment.room_id][assignment.date] = {
          assignment_id: assignment.assignment_id,
          doctor_id: assignment.doctor_id,
          doctor_name: assignment.doctor_name,
          color: assignment.doctor_id ? doctors.find(doctor => doctor.doctor_id === assignment.doctor_id)?.color || '#999' : '#f1f1f1c9'
        };
      });
      setAssignments(mapped);
    })
    mapAssignments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset, location, doctors, token])

///////DRAG AND DROP HANDLERS
  const handleDragStart = (event, doctor) => {
    event.dataTransfer.setData('doctor_id', doctor.doctor_id);
    event.dataTransfer.setData('doctor_name', doctor.doctor_name);
    event.dataTransfer.setData('color', doctor.color)
    // setDraggingDr(doctor);
  }
  const handleDragEnter = (room, dayIso) => {
    setHoveredCell({ room, dayIso })
  };

  const handleDragLeave = (room, dayIso) => {
    if (hoveredCell?.room?.room_id === room.room_id && hoveredCell?.dayIso === dayIso) {
      setHoveredCell(null);
    }
  };

  //Droppable functionality - refactored to save via POST
  const handleDrop = async (event, room, dayIso) => {
    event.preventDefault();
    const doctorId = event.dataTransfer.getData('doctor_id')
    const doctorName = event.dataTransfer.getData('doctor_name');
    const color = event.dataTransfer.getData('color');

    if (!doctorId) {
      console.error('No doctor selected for drop');
      return;
    }

    setAssignments(prev => ({
      ...prev,
      [room.room_id]: {
        ...prev[room.room_id],
        [dayIso]: {
          assignment_id: prev[room.room_id]?.[dayIso]?.assignment_id || null,
          doctor_id: doctorId,
          doctor_name: doctorName,
          color
        },
      },
    }));
    setHoveredCell(null);

    try {
      const updatedAssignment = await saveAssignment(dayIso, room.room_id, doctorId, token);

      if (updatedAssignment) {
        setAssignments(prev => ({
          ...prev,
          [room.room_id]: {
            ...prev[room.room_id],
            [dayIso]: {
              assignment_id: updatedAssignment.assignment_id,
              doctor_id: updatedAssignment.doctor_id,
              doctor_name: updatedAssignment.doctor_name,
              color: doctors.find(doctor => doctor.doctor_id === updatedAssignment.doctor_id)?.color || color,
            }
          }
        }))
      }
    } catch (error) {
      console.error('Save assignment failed', error);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  }

  // close context menu if clicking away
  useEffect(() => {
    const handleClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setContextMenu(null)
      }
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick)
}, []);

if (!location?.location_id) return <p>loading...</p> 
if (rooms.length === 0) return <p>no rooms associated with your location.  please contact your administrator</p>
if (doctors.length === 0) return <p>no doctors associated with your location.  please contact your administrator</p>

  return (
    <div className='flex flex-col md:flex-row'>

      {/* Doctor List  */}
      {isMobile ? (
        <div className='sticky top-0 bg-gray-50 z-10 p-2 overflow-x-auto flex gap-2 border-b shadow-sm'>
          {doctors.map(doctor => (
            <div key={doctor.doctor_id} draggable onDragStart={(event) => handleDragStart(event, doctor)}  className={`${doctor.bgClass} px-3 py-1 rounded text-white cursor-grab select-none text-sm`}>
              {doctor.doctor_name}
            </div>
          ))}
          </div>
        ) : (
        <div className='flex-shrink-0 w-36 md:w-48 sticky top-2 h-[60vh] overflow-y-auto space-y-3 p-2 bg-gray-50 rounded shadow-md'>
          {doctors.map(doctor => (
            <div key={doctor.doctor_id} draggable onDragStart={(event) => handleDragStart(event, doctor)}  className={`${doctor.bgClass} px-3 py-1 rounded text-white cursor-grab select-none text-sm`}>
              {doctor.doctor_name}
            </div>
          ))}
        </div>

        )}

      <div className='overflow-x-auto overflow-y-auto max-h-[60vh] p-2'>

        <div className='flex justify-center gap-5 items-center mb-2'>
          <button onClick={prevWeek} className='px-2 py-1 bg-docpgrPurple60 hover:bg-docpgrPurple90 text-white font-semibold rounded-md'>Prev Week</button>
          <span className='font-semibold text-sm md:text-base'>
            Week of {daysOfWeek[0].date} - {daysOfWeek[6].date}
          </span>
          <button onClick={nextWeek} className='px-2 py-1 bg-docpgrPurple60 hover:bg-docpgrPurple90 text-white font-semibold rounded-md'>Next Week</button>
        </div>

        {/* Calendar  */}
        <div className='grid' style={{ gridTemplateColumns: 'minmax(100px, 1fr) repeat(7, minmax(70px, 1fr))', gap: '6px', minWidth: '600px' }}>
          <div></div>
          {daysOfWeek.map(day => (
            <div key={day.iso} className='font-semibold text-center text-xs md:text-sm'>
              <div>
                {day.label}
              </div>
              <div>
                {day.date}
              </div>
            </div>
          ))}

          {rooms.map((room) => (
            <React.Fragment key={room.room_id}>
              <div className='font-semibold text-center text-xs md:text-sm flex items-center justify-center'>
                {room.room_name}
              </div>
              {daysOfWeek.map((day) => {
                const assignment = assignments[room.room_id]?.[day.iso];
                return (
                  <div key={`${room.room_id}-${day.iso}`} onTouchStart={(event) => handleTouchStart(event, room, day.iso, assignment, touchTimer, setContextMenu)} onTouchEnd={() => handleTouchEnd(touchTimer)} onDrop={(event) => handleDrop(event, room, day.iso)} onDragOver={handleDragOver} onDragEnter={() => handleDragEnter(room, day.iso)} onDragLeave={() => handleDragLeave(room, day.iso)} onContextMenu={(event) => handleRightClick(event, room, day.iso, assignment, setContextMenu)} className='h-16 md:h-20 text-xs md:text-sm flex items-center justify-center font-semibold text-white rounded border transition-all' style={{ backgroundColor: hoveredCell?.room?.room_id === room.room_id && hoveredCell?.dayIso === day.iso ? assignment?.color : assignment?.color || '#f1f1f1c9' }}>
                    {assignment?.doctor_name || ''}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        {/* Delete Assignment Menu  */}
          {contextMenu &&
            createPortal(
              <div ref={menuRef} style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x, zIndex: 9999 }} className='bg-white border rounded-lg shadow-md p-2'>
                <button onClick={() => { handleDelete(contextMenu.room, contextMenu.dayIso, contextMenu.assignment.assignment_id, setAssignments, token); setContextMenu(null); }} className='block w-full text-left px-4 py-2 text-red-600 hover:bg-red-100 rounded'>
                  Delete Assignment
                </button>
                <button onClick={() => setContextMenu(null)} className='block w-full text-left px-4 py-2 hover:bg-gray-100 rounded'>
                  Cancel
                </button>
              </div>,
              document.body
            )}
      </div>
    </div>
  );
}
